import { Component, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'; // cspell:disable-line
import { CheckboxModule } from 'primeng/checkbox'; // cspell:disable-line
import { ChartModule } from 'primeng/chart'; // cspell:disable-line
import { TooltipModule } from 'primeng/tooltip'; // cspell:disable-line
import { LucideAngularModule } from 'lucide-angular';
import { FormatCO2ePipe } from 'src/app/pipes/format-co2e.pipe';
import { DurationFormatPipe } from 'src/app/pipes/duration-format.pipe';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import { EcoScoreService } from 'src/app/shared/services/eco-score.service';
import { EcoscoreComponent } from 'src/app/travel-board/components/ecoscore/ecoscore.component';
import { ChartDoughnutComponent } from '../../chart-doughnut/chart-doughnut.component';
import {
  DirectFlightTipData,
  EcoAccommodationTipData,
  HighEmissionActivityTipData,
  ReductionTip,
  TransportAlternativeTipData,
  TripDurationTipData,
} from '../../../models/reduction-tip';
import {
  ALTERNATIVE_MODES,
  AlternativeMode,
  getAlternativeEmissionsPerPerson,
  getBestAlternativeMode,
  isSelectableTip,
  TIP_I18N_KEY,
  TIP_ICON,
} from '../../../utils/reduction-tip-savings';
import { ReductionTipsService } from '../../../services/reduction-tips.service';

/** Emissions kept, then emissions avoided — same palette as the page donuts. */
const EMISSIONS_COLOR = '#6C9587';
const SAVINGS_COLOR = '#CFD2CF';

const ALTERNATIVE_ICON: Record<AlternativeMode, string> = {
  train: 'tram-front',
  carpooling: 'car',
  bus: 'bus',
};

/** Doughnuts in the expanded detail stay small — the row must remain compact. */
const DETAIL_CHART_SIZE = 70;
const DETAIL_CHART_THICKNESS = 12;

@Component({
  selector: 'app-reduction-tip-item',
  standalone: true,
  imports: [
    FormsModule,
    TranslocoModule,
    CheckboxModule,
    ChartModule,
    TooltipModule,
    LucideAngularModule,
    ChartDoughnutComponent,
    EcoscoreComponent,
    FormatCO2ePipe,
  ],
  providers: [DurationFormatPipe],
  templateUrl: './reduction-tip-item.component.html',
})
export class ReductionTipItemComponent {
  tip = input.required<ReductionTip>();
  /** Inside a group the type title is already displayed by the group header. */
  nested = input(false);

  private translocoService = inject(TranslocoService);
  private durationPipe = inject(DurationFormatPipe);
  private ecoScoreService = inject(EcoScoreService);
  private tipsService = inject(ReductionTipsService);
  formatCo2e = inject(FormatCo2eService);

  /** Selection and chosen alternative both live in the dialog state. */
  selected = computed(() => this.tipsService.isSelected(this.tip().id));
  mode = computed(() => this.tipsService.modeOf(this.tip().id));
  travellers = this.tipsService.travellers;
  /** Display figures per traveller rather than for the whole trip. */
  perPerson = this.tipsService.perPerson;

  readonly chartSize = DETAIL_CHART_SIZE;
  readonly chartThickness = DETAIL_CHART_THICKNESS;
  readonly emissionsColor = EMISSIONS_COLOR;
  readonly savingsColor = SAVINGS_COLOR;

  expanded = signal(false);

  icon = computed(() => TIP_ICON[this.tip().type]);
  i18nKey = computed(() => `reductionTips.${TIP_I18N_KEY[this.tip().type]}`);
  selectable = computed(() => isSelectableTip(this.tip()));

  /** Nested rows sit inside a bordered group, so they stay lighter. */
  containerClass = computed(() => {
    if (this.selected()) return 'border-primary-500 bg-primary-50';
    return this.nested()
      ? 'border-primary-100 bg-white'
      : 'border-primary-200 bg-white';
  });

  /** kg CO₂e avoided over the whole trip by this single tip. */
  savings = computed(() => this.tipsService.savingOf(this.tip()));

  /** Travellers on the trip, never zero — every conversion divides by it. */
  private headcount = computed(() => Math.max(1, this.travellers()));

  displaySavings = computed(() => this.fromTotal(this.savings()));

  /** Element the tip applies to — what distinguishes two tips of one type. */
  subtitle = computed(() => {
    const tip = this.tip();
    switch (tip.type) {
      case 'transport_alternative':
      case 'direct_flight':
        return this.getTransportSubtitle(
          tip.data as TransportAlternativeTipData | DirectFlightTipData,
        );
      case 'eco_accommodation':
        return this.getAccommodationSubtitle(
          tip.data as EcoAccommodationTipData,
        );
      case 'high_emission_activity':
        return this.getActivitySubtitle(
          tip.data as HighEmissionActivityTipData,
        );
      default:
        return '';
    }
  });

  alternativeData = computed(() =>
    this.tip().type === 'transport_alternative'
      ? (this.tip().data as TransportAlternativeTipData)
      : null,
  );

  directFlightData = computed(() =>
    this.tip().type === 'direct_flight'
      ? (this.tip().data as DirectFlightTipData)
      : null,
  );

  accommodationData = computed(() =>
    this.tip().type === 'eco_accommodation'
      ? (this.tip().data as EcoAccommodationTipData)
      : null,
  );

  /** Transport tip current emissions, on the basis currently displayed. */
  alternativeCurrent = computed(() => {
    const data = this.alternativeData();
    return data ? this.fromPerPerson(data.currentEmissionsPerPerson) : 0;
  });

  /** Direct-flight figures — the API sends them per traveller. */
  directFlightValues = computed(() => {
    const data = this.directFlightData();
    if (!data) return null;
    return {
      current: this.fromPerPerson(data.currentEmissions),
      direct: this.fromPerPerson(data.directEmissions),
      savings: this.fromPerPerson(data.savings),
      savingsPercent: data.savingsPercent,
    };
  });

  /** Accommodation figures — the API sends them as trip totals. */
  accommodationValues = computed(() => {
    const data = this.accommodationData();
    if (!data) return null;
    return {
      current: this.fromTotal(data.currentEmissions),
      eco: this.fromTotal(data.ecoEmissions),
      savings: this.fromTotal(data.savings),
      savingsPercent: data.savingsPercent,
    };
  });

  durationData = computed(() =>
    this.tip().type === 'trip_duration'
      ? (this.tip().data as TripDurationTipData)
      : null,
  );

  /** Alternative actually simulated: the user's choice, else the best one. */
  effectiveMode = computed<AlternativeMode | null>(() => {
    const data = this.alternativeData();
    if (!data) return null;
    return this.mode() ?? getBestAlternativeMode(data);
  });

  /** The three alternatives, cheapest first, ready to render. */
  alternatives = computed(() => {
    const data = this.alternativeData();
    if (!data) return [];

    const current = this.fromPerPerson(data.currentEmissionsPerPerson);

    return ALTERNATIVE_MODES.map((mode) => {
      const emissions = this.fromPerPerson(
        getAlternativeEmissionsPerPerson(data, mode),
      );
      return {
        mode,
        icon: ALTERNATIVE_ICON[mode],
        labelKey: `reductionTips.transportAlternative.${mode}`,
        emissions,
        chart: [
          { value: emissions, color: EMISSIONS_COLOR, label: '' },
          {
            value: Math.max(0, current - emissions),
            color: SAVINGS_COLOR,
            label: '',
          },
        ],
      };
    }).sort((a, b) => a.emissions - b.emissions);
  });

  toggleExpanded(): void {
    this.expanded.update((value) => !value);
  }

  toggleSelected(): void {
    this.tipsService.setSelection([this.tip().id], !this.selected());
  }

  /** Choosing an alternative also selects the tip — that is the intent. */
  selectAlternative(mode: AlternativeMode): void {
    this.tipsService.setMode(this.tip().id, mode);
    if (!this.selected()) this.tipsService.setSelection([this.tip().id], true);
  }

  singleValueChart(value: number) {
    return [{ value, color: EMISSIONS_COLOR, label: '' }];
  }

  savingsChart(remaining: number, savings: number) {
    return [
      { value: remaining, color: EMISSIONS_COLOR, label: '' },
      { value: Math.max(0, savings), color: SAVINGS_COLOR, label: '' },
    ];
  }

  co2eValue(value: number): string {
    return this.formatCo2e.formatValue(value);
  }

  /** Value and unit on the displayed basis, for interpolated sentences. */
  co2e(value: number): string {
    return this.formatCo2e.format(value);
  }

  /** The dialog toggle states the basis, so units stay bare — /pers on every
   * figure would only clutter the cards. */
  co2eUnit(value: number): string {
    return this.formatCo2e.getUnit(value);
  }

  /**
   * The API mixes bases: `transport_alternative` and `direct_flight` carry
   * per-traveller values while `eco_accommodation` carries trip totals. Both
   * helpers bring a raw value onto whatever basis the dialog displays.
   */
  fromPerPerson(value: number): number {
    return this.perPerson() ? value : value * this.headcount();
  }

  fromTotal(value: number): number {
    return this.perPerson() ? value / this.headcount() : value;
  }

  round(value: number): number {
    return Math.round(value);
  }

  getDurationScores(data: TripDurationTipData): number[] {
    return [
      data.currentIntensity,
      ...data.projections.map((p) => p.intensity),
    ].map((v) => this.ecoScoreService.getEcoScore(v));
  }

  getDurationChartData(data: TripDurationTipData): object {
    return {
      labels: [
        `${data.currentDays}j`,
        ...data.projections.map((p) => `${p.totalDays}j`),
      ],
      datasets: [
        {
          data: [
            data.currentIntensity,
            ...data.projections.map((p) => p.intensity),
          ],
          backgroundColor: [
            EMISSIONS_COLOR,
            ...data.projections.map(() => '#B8D4CC'),
          ],
          borderRadius: 4,
          barPercentage: 0.6,
        },
      ],
    };
  }

  getDurationChartOptions(): object {
    return {
      responsive: true,
      plugins: { legend: { display: false } },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { callback: (v: number) => `${Math.round(v)} kg` },
          grid: { color: '#E8F0ED' },
        },
        x: { grid: { display: false } },
      },
    };
  }

  private getTransportSubtitle(
    data: TransportAlternativeTipData | DirectFlightTipData,
  ): string {
    if ('directEmissions' in data) {
      const name = this.translocoService.translate(
        `categoryType.${data.transportType}`,
      );
      return `${name} — ${data.flightRoute}`;
    }
    const name =
      data.transportTitle ??
      this.translocoService.translate(`categoryType.${data.transportType}`);
    if (data.flightRoute) return `${name} — ${data.flightRoute}`;
    if (data.transportDuration)
      return `${name} — ${this.durationPipe.transform(data.transportDuration)}`;
    return `${name} — ${this.round(data.distance)} km`;
  }

  private getAccommodationSubtitle(data: EcoAccommodationTipData): string {
    return (
      data.accommodationTitle ??
      this.translocoService.translate(`categoryType.${data.accommodationType}`)
    );
  }

  private getActivitySubtitle(data: HighEmissionActivityTipData): string {
    return (
      data.activityTitle ??
      this.translocoService.translate(`categoryType.${data.activityType}`)
    );
  }
}
